import React from "react";
import _ from "lodash";
import styled, { css } from "styled-components";

import {
  CreateCustomerBranchFilterAttributes,
  CreateProductReplacementParams,
  CustomerBranchFilterDefault,
  CustomerBranchFilterType
} from "src/core/products/api/products.api";
import { IOpenDay } from "src/modules/shutdowns/api/open-day.api";
import { ICustomer } from "src/core/customers/api/customers.api";
import useTerminology from "src/core/shared/hooks/useTerminology";

import { Text } from "../Text";
import CustomerBranchFilterRow from "./CustomerBranchFilterRow";
import { Button } from "../Button";
import { Icons } from "../Icon/types";

export enum CustomerBranchFilterAction {
  OpenDay = "open day",
  ProductReplacement = "product replacement",
  LeadTimeOverride = "lead time override"
}

interface CustomerBranchFiltersProps {
  form: Partial<IOpenDay> | Partial<CreateProductReplacementParams>;
  setSelectedCustomer: (customer: ICustomer | null) => void;
  setShowFiltersModal: (show: boolean) => void;
  removeFilter: (filter: CreateCustomerBranchFilterAttributes) => void;
  action: CustomerBranchFilterAction
}

const CustomerBranchFilters: React.ReactFCWithChildren<CustomerBranchFiltersProps> = ({
  form, setSelectedCustomer, setShowFiltersModal, removeFilter, action
}) => {
  const terminology = useTerminology();

  const topLevelFilters = React.useMemo(() => {
    return _.sortBy(_.filter(form.filters, nestedFilter => {
      if (nestedFilter.customerId) {
        return true;
      }

      if (form.type === CustomerBranchFilterDefault.AllowAll && nestedFilter.filterType === CustomerBranchFilterType.Include) {
        return false;
      } else if (form.type === CustomerBranchFilterDefault.AllowNone && nestedFilter.filterType === CustomerBranchFilterType.Exclude) {
        return false;
      }

      return true;
    }), "customerId");
  }, [ form.filters, form.type ]);

  const headerText = React.useMemo(() => {
    switch (action) {
      case CustomerBranchFilterAction.OpenDay:
        return form.type === CustomerBranchFilterDefault.AllowAll ? "Everyone can order on this day apart from those listed below" : "Only those listed below can order on this day";
      case CustomerBranchFilterAction.ProductReplacement:
        return form.type === CustomerBranchFilterDefault.AllowAll ? "Everyone's orders will be affected by this product replacement" : "Only orders for those listed below will be affected by this product replacement";
      case CustomerBranchFilterAction.LeadTimeOverride:
        return form.type === CustomerBranchFilterDefault.AllowAll ? "Everyone will be affected by this override" : "Only customers listed below will be affected by this override";
    }
  }, [ action, form.type ]);

  return (
    <FiltersContainer>
      <div className="filters-box">
        <Text as="h3">
          {headerText}
        </Text>

        {_.map(topLevelFilters, filter => (
          <div
            className="filter-row"
            key={`${filter.filterType}-${filter.customerId || filter.branchId}`}
          >

            <CustomerBranchFilterRow
              className={filter.filterType}
              filter={filter}
              setSelectedCustomer={setSelectedCustomer}
              setShowFiltersModal={setShowFiltersModal}
              removeFilter={removeFilter}
              action={action}
            />

            <div className="filter-row-children">
              {_.filter(form.filters, nestedFilter => {
                if (!nestedFilter.branchId) {
                  return false;
                }

                if (nestedFilter.branch?.customerId !== filter.customerId) {
                  return false;
                }

                if (form.type === CustomerBranchFilterDefault.AllowAll && nestedFilter.filterType === CustomerBranchFilterType.Include) {
                  return true;
                } else if (form.type === CustomerBranchFilterDefault.AllowNone && nestedFilter.filterType === CustomerBranchFilterType.Exclude) {
                  return true;
                }

                return false;
              }).map(f => (
                <div key={`filter-row-${f.branchId}-${f.customerId}`}>
                  {f.branch && (
                    <CustomerBranchFilterRow
                      className={f.filterType}
                      filter={f}
                      removeFilter={removeFilter}
                      action={action}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <Button
          onClick={() => {
            if (setShowFiltersModal) {
              setShowFiltersModal(true);
            }
          }}
          $buttonType="plain"
          icon={Icons.ADD}
        >
          {form.type === CustomerBranchFilterDefault.AllowAll ? `Exclude some ${terminology?.customer.plural.text} or ${terminology?.drop.plural.text}` : `Add ${terminology?.customer.plural.text} and ${terminology?.drop.plural.text} to include`}
        </Button>
      </div>
    </FiltersContainer>
  );
};

export default CustomerBranchFilters;

const FiltersContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    margin: ${theme.spacing.margins.md} 0;

    .filters-box {
      background: ${theme.colors.white};
      width: 100%;
      display: flex;
      flex-direction: column;
      padding: ${theme.spacing.padding.md} ${theme.spacing.padding.sm};

      .filter-row {
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .filter-row-children {
          display: flex;
          flex-direction: column;
          margin-left: ${theme.spacing.margins.lg};
          margin-right: ${theme.spacing.margins.lg};
        }
      }
    }
  `}
`;
